/**
 * Main orchestrator hook for the game player.
 *
 * Responsibilities:
 * - Fetches story, threads, and beats via TanStack Query
 * - Creates or resumes a server-side player session
 * - Wires up the useThreadPlayer state machine for beat navigation
 * - Fires off server mutations (record response, update position, generate AI
 *   content) as fire-and-forget — they never block the UI
 * - Look-ahead: pre-generates the next response beat while the user reads the
 *   current one, skipped when the current beat requires user input since the
 *   response content depends on their choice
 * - Subscribes to ActionCable for streamed narrative content with HTTP fallback
 *
 * @module useGameSession
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { storyService } from '../services/storyService'
import { playerSessionService } from '../services/playerSessionService'
import { teamService } from '../services/teamService'
import { useThreadPlayer } from '../lib/player'
import { useBeatContentStream } from './useBeatContentStream'
import { checkInputQuality } from '../lib/inputQuality'
import { isFallbackCharacterResponse } from '../lib/responseParser'
import { notifications } from '@mantine/notifications'

/**
 * Check if generated content data is actually renderable — non-empty string
 * or non-empty array. Rejects objects, null, empty strings, and empty arrays
 * that would shadow the streaming text fallback.
 */
function isRenderableContent(data) {
  if (typeof data === 'string') return data.length > 0
  if (Array.isArray(data)) return data.length > 0
  return false
}

/**
 * Should the HTTP-returned `data` overwrite an existing `previous` cached
 * value? Returns false when `data` is the canonical "characters pause"
 * fallback the backend emits on parse failure AND we already have renderable
 * streamed text — in that case the live stream is more useful than the
 * generic Narrator placeholder.
 */
function shouldReplaceWithHttpData(data, previous) {
  if (!isRenderableContent(data)) return false
  if (isFallbackCharacterResponse(data) && isRenderableContent(previous?.data)) return false
  return true
}

/**
 * @param {string|null} storyIdParam - Story to play. In review mode this is
 *   ignored (derived from the loaded session instead) and may be null.
 * @param {{reviewSessionId?: string|number}} [opts] - When reviewSessionId is
 *   set the hook enters READ-ONLY review mode: it loads that completed session
 *   by id (never creates/reopens one), suppresses every mutation
 *   (position/response/complete), hydrates AI content from the session's stored
 *   `generated` responses so nothing regenerates, and unlocks the whole story
 *   map for free navigation. Used by the /review/:sessionId route so students
 *   can walk a finished story without changing any answer.
 */
export function useGameSession(storyIdParam, opts = {}) {
  const reviewSessionId = opts.reviewSessionId ?? null
  const reviewMode = reviewSessionId != null
  const [currentThreadIndex, setCurrentThreadIndex] = useState(0)
  const [goToLastBeat, setGoToLastBeat] = useState(false)
  const [generatedContent, setGeneratedContent] = useState({})
  const [selectedChoice, setSelectedChoice] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [submittedInputs, setSubmittedInputs] = useState({})
  // Choices the player has committed by advancing past a decision beat. Keyed by
  // beat id. Distinct from `selectedChoice` (the in-progress, not-yet-committed
  // pick on the current edge beat). Once a beat id lands here it is "locked":
  // revisiting shows the choice read-only and Next just advances without
  // re-recording or regenerating downstream content. Reset per playthrough.
  const [committedChoices, setCommittedChoices] = useState({})
  // The live edge — the furthest position the player has actually progressed to,
  // as a (thread index, beat index) pair. This is the boundary for review:
  // anything at or before it has been seen and can be revisited; nothing past it
  // can be reached except by genuinely advancing. It never regresses while the
  // player browses earlier threads, and "skip to current" returns here.
  const [liveThreadIndex, setLiveThreadIndex] = useState(0)
  const [liveBeatIndex, setLiveBeatIndex] = useState(0)
  // Pending cross-thread jump target. When the player jumps into a different
  // thread we switch currentThreadIndex first, then apply this beat index once
  // that thread's beats have loaded (mirrors the goToLastBeat mechanism).
  const [pendingBeatIndex, setPendingBeatIndex] = useState(null)
  const [isSessionComplete, setIsSessionComplete] = useState(false)
  // True when the player has finished every RELEASED thread but the story still
  // has unreleased threads ahead (facilitator hasn't released the next session
  // yet). Distinct from isSessionComplete: the session must stay in_progress so
  // it re-opens the moment the next thread is released. Marking it complete here
  // is the "stuck after release" bug — a whole cohort got frozen at Session 2.
  const [isWaitingForRelease, setIsWaitingForRelease] = useState(false)
  // Title card shown when entering a thread (orients the player that a new
  // thread has begun). Dismissed by its Next button. Suppressed on mid-thread
  // resume and on backward cross-thread navigation — see the gates below.
  const [showTitleCard, setShowTitleCard] = useState(true)

  // Review mode: load the completed session by id via the read-only `show`
  // endpoint. This must NEVER go through startOrResumeSession/create — that
  // action reopens a completed session (flips it back to in_progress), which is
  // exactly what review must not do. The story id is derived from the loaded
  // session so all downstream story/threads/beats queries key off it.
  const { data: reviewSession, isLoading: reviewSessionLoading } = useQuery({
    queryKey: ['playerSession', reviewSessionId],
    queryFn: () => playerSessionService.getPlayerSession(reviewSessionId),
    enabled: reviewMode,
    staleTime: Infinity,
  })

  // The story being played/reviewed. Live play gets it from the route param; a
  // review derives it from the loaded session.
  const storyId = reviewMode ? (reviewSession?.story_id ?? null) : storyIdParam

  // Fetch story
  const { data: story, isLoading: storyLoading } = useQuery({
    queryKey: ['story', storyId],
    queryFn: () => storyService.getStory(storyId),
    enabled: !!storyId,
  })

  // Team identity is carried in the URL, not a persisted side-channel: a team
  // launch routes to /play/:storyId?team=<teamId>&ts=<teamStoryId>. Solo play
  // has no params. The URL being the single source of truth means there's
  // nothing to go stale across a team's sequence of stories — the class of bug
  // where a prior story's team_story_id leaked into a later launch (and the
  // backend 403'd it into a dead end) is structurally impossible.
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const teamContext = useMemo(() => {
    const teamId = searchParams.get('team')
    const teamStoryId = searchParams.get('ts')
    if (!teamId || !teamStoryId) return null
    return { teamId, teamStoryId }
  }, [searchParams])

  // Start or resume server session. Including team context in the queryKey
  // means switching from a solo to a team-tagged session for the same story
  // refetches rather than reusing a stale cached id. Disabled in review mode —
  // review never creates/reopens a session; it reads reviewSession instead.
  const { data: liveServerSession, error: sessionError } = useQuery({
    queryKey: ['playerSession', storyId, teamContext?.teamStoryId ?? null],
    queryFn: () => playerSessionService.startOrResumeSession(storyId, teamContext),
    enabled: !reviewMode && !!storyId,
    staleTime: Infinity,
    // A 403 is the backend rejecting a team context that doesn't match the story
    // (a hand-edited or stale link) — deterministic, so don't retry it; we
    // recover by dropping the team params below. Other errors get one retry.
    retry: (failureCount, error) =>
      error?.response?.status === 403 ? false : failureCount < 1,
  })

  // The session driving the player: the read-only completed session in review
  // mode, otherwise the live create/resume session.
  const serverSession = reviewMode ? reviewSession : liveServerSession

  // At most ONE automatic URL correction per story mount. Both effects below can
  // change the URL; without a shared one-shot guard they ping-pong forever — the
  // 403-recovery strips params, the auto-redirect re-adds them from the resolved
  // session, which 403s again, ad infinitum (URL bar + role badge flashing, a
  // flood of 403s, the story never stabilizing). One correction is all a
  // legitimate case ever needs; anything more is a loop, so we hard-stop it.
  const autoNavDoneRef = useRef(false)
  useEffect(() => {
    // Reset the guard when the story changes so a fresh navigation can correct.
    autoNavDoneRef.current = false
  }, [storyId])

  // Recover from a rejected team context instead of dead-ending. If the launch
  // 403s (stale/edited link, or membership the player no longer has), strip the
  // team params and fall back to a solo launch — and STAY solo (the guard below
  // blocks the auto-redirect from re-adding params). The backend still resolves
  // the player's existing in-progress session for this story, so they resume
  // (solo-framed) rather than loop or stare at an empty screen.
  useEffect(() => {
    if (reviewMode) return
    if (autoNavDoneRef.current) return
    if (!teamContext) return
    if (sessionError?.response?.status !== 403) return
    // A team_play_required 403 means the group has solo play disabled — don't
    // fall back to solo (that solo POST is exactly what was refused), send them
    // to their team door instead. Handled by the effect below.
    if (sessionError?.response?.data?.team_play_required) return
    autoNavDoneRef.current = true
    navigate(`/play/${storyId}`, { replace: true })
  }, [teamContext, sessionError, storyId, navigate])

  // Solo-play gate: the player's group has solo play turned off (team cohort).
  // A solo launch 403s with team_play_required — steer them to the team list
  // (the only door that tags the session as team play) rather than dead-ending
  // on an empty session. Fires once; works whether or not team params were in
  // the URL (a stale team link that 403s here also lands them on /teams).
  useEffect(() => {
    if (reviewMode) return
    if (autoNavDoneRef.current) return
    if (sessionError?.response?.status !== 403) return
    if (!sessionError?.response?.data?.team_play_required) return
    autoNavDoneRef.current = true
    navigate('/teams', { replace: true })
  }, [sessionError, navigate])

  // Self-correct a paramless entry into a team session. A player can reach
  // /play/:storyId with no team params via the story page, a bookmark, or any
  // direct link; the backend resolves their existing in-progress session (team
  // tag intact). When that resumed session is team-tagged, rewrite the URL to
  // the canonical team form so the indicator, role pool, and "Back to Team"
  // resolve — they still land on their saved beat. Runs at most once (the shared
  // guard), and never after a 403 recovery, so it can't re-create a bad URL.
  useEffect(() => {
    if (reviewMode) return
    if (autoNavDoneRef.current) return
    if (teamContext) return
    if (!serverSession?.team_id || !serverSession?.team_story_id) return
    autoNavDoneRef.current = true
    const params = new URLSearchParams({
      team: String(serverSession.team_id),
      ts: String(serverSession.team_story_id),
    })
    navigate(`/play/${storyId}?${params.toString()}`, { replace: true })
  }, [teamContext, serverSession?.team_id, serverSession?.team_story_id, storyId, navigate])

  // Role gating (Hult): when the story has the role mechanic enabled and this
  // session hasn't picked one yet, the player must choose before playing.
  // Role is per-session — resume never re-prompts, a new playthrough does.
  const queryClient = useQueryClient()
  const playerRoles = story?.options?.player_roles_enabled === true
    ? (story?.options?.player_roles || [])
    : []
  // Never prompt for a role in review — the completed session already has one
  // (or never used the mechanic). Prompting would also require a live session.
  const needsRoleSelection =
    !reviewMode && playerRoles.length > 0 && !!serverSession && !serverSession.role

  // For team play, surface which roles are already taken (or at their cap) so
  // the picker can disable them. The set_role endpoint is the authoritative
  // guard (it 422s on a blocked pick); this is purely a UX hint. Only fetched
  // while a role still needs choosing in a team-tagged session.
  const { data: teamRoleStatus } = useQuery({
    queryKey: ['teamRoleStatus', teamContext?.teamId ?? null, teamContext?.teamStoryId ?? null],
    queryFn: () => teamService.getTeam(teamContext.teamId),
    enabled: needsRoleSelection && !!teamContext?.teamId,
    staleTime: 5_000,
    // Keep the picker live while the player is still choosing: teammates claim
    // roles concurrently at session start, and an early, deliberate picker would
    // otherwise see a frozen list, choose a role taken out from under them, and
    // hit a 422 dead-end. Polling (only while needsRoleSelection is true, so it
    // stops the moment a role is set) disables claimed roles in near-real-time.
    refetchInterval: needsRoleSelection && teamContext?.teamId ? 5_000 : false,
    refetchIntervalInBackground: false,
    select: (team) => team?.current_story || null,
  })

  // Team name for the solo/team play indicator. Reuses the same ['team', id]
  // query key as useTeam(id), so a player arriving from the team dashboard hits
  // a warm cache (no extra request in the common path). Only the name is needed.
  const { data: teamName } = useQuery({
    queryKey: ['team', teamContext?.teamId ?? null],
    queryFn: () => teamService.getTeam(teamContext.teamId),
    enabled: !!teamContext?.teamId,
    staleTime: 30_000,
    select: (team) => team?.name || null,
  })

  // Role keys the player cannot pick: cap reached given live claims. Default
  // cap is 1 (unique). A non-positive cap means unlimited.
  const takenRoleKeys = useMemo(() => {
    const cs = teamRoleStatus
    if (!cs) return []
    const caps = cs.role_caps || {}
    const claims = cs.role_claims || {}
    return playerRoles
      .map((r) => r.key)
      .filter((key) => {
        const rawCap = key in caps ? Number(caps[key]) : 1
        if (rawCap <= 0) return false // unlimited
        return (claims[key] || 0) >= rawCap
      })
  }, [teamRoleStatus, playerRoles])

  const roleMutation = useMutation({
    mutationFn: (role) => playerSessionService.setRole(serverSession.id, role),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        ['playerSession', storyId, teamContext?.teamStoryId ?? null],
        (prev) => ({ ...(prev || {}), role: updated.role }),
      )
      // Documents are filtered by role server-side. The docs query is keyed on
      // the role, so setting it normally drives a fresh fetch on its own; this
      // prefix invalidation also clears any roleless-keyed entry cached before
      // the role resolved (the case that left replayed sessions on a stale list).
      queryClient.invalidateQueries({ queryKey: ['storyDocuments'] })
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Could not set role',
        message: error.response?.data?.error || 'Please try again.',
      })
      // A 422 here means a teammate claimed this role while the player was
      // deciding — their picker is showing a stale availability list. Refetch
      // the team role status so the just-taken role (and any others claimed in
      // the meantime) immediately render disabled and they can pick a free one,
      // instead of re-submitting the same taken role into another 422.
      queryClient.invalidateQueries({
        queryKey: ['teamRoleStatus', teamContext?.teamId ?? null, teamContext?.teamStoryId ?? null],
      })
    },
  })

  // Subscribe to ActionCable for streamed narrative content
  const { streamingContent, clearBeats } = useBeatContentStream(serverSession?.id)

  // Merge completed streaming content into generatedContent state.
  // Uses functional setState so the guard reads the latest state without
  // needing generatedContent in the dependency array (avoids stale closure).
  useEffect(() => {
    if (!streamingContent) return
    for (const [beatId, entry] of Object.entries(streamingContent)) {
      if (entry.complete) {
        setGeneratedContent((prev) => {
          // Don't overwrite if HTTP response already landed with renderable canonical data.
          // Empty/invalid HTTP data should be overwritten by streaming text.
          if (isRenderableContent(prev[beatId]?.data) && !prev[beatId]?.loading) return prev
          return {
            ...prev,
            [beatId]: { data: entry.text, loading: false },
          }
        })
      }
    }
  }, [streamingContent])

  // Latch guarding against the resume position clobber (see the resolution logic
  // further down, near resumeStartIndex). Declared here so handlePositionChange
  // can read it. Starts closed (suppress writes); opens once restore is resolved
  // and never closes again.
  const restoreResolvedRef = useRef(false)

  // Restore position from server session
  const [hasRestoredPosition, setHasRestoredPosition] = useState(false)
  useEffect(() => {
    if (serverSession && !hasRestoredPosition) {
      // Review starts the walk-through at the very beginning (thread 0, beat 0),
      // NOT the saved final position — the student wants to re-walk the whole
      // story. The live edge is instead pushed to the story's end below (once
      // threads load) so every position is reachable for free navigation.
      if (!reviewMode) {
        if (serverSession.current_thread_index > 0) {
          setCurrentThreadIndex(serverSession.current_thread_index)
          // Seed the live edge to the resumed thread so the player can review
          // every earlier thread immediately, not just what they re-walk.
          setLiveThreadIndex(serverSession.current_thread_index)
        }
        if (serverSession.current_beat_index > 0) {
          setLiveBeatIndex(serverSession.current_beat_index)
        }
      }
      // Hydrate committed answers from the server so revisiting beats from a
      // resumed session shows them locked, just like answers made this session.
      const responses = serverSession.responses || []
      const restoredChoices = responses
        .filter((r) => r.response_type === 'choice' && r.response_data?.choice_id != null)
        .reduce((acc, r) => ({
          ...acc,
          [r.beat_id]: { id: r.response_data.choice_id, text: r.response_data.choice_text },
        }), {})
      const restoredInputs = responses
        .filter((r) => r.response_type === 'text' && typeof r.response_data?.text === 'string')
        .reduce((acc, r) => ({ ...acc, [r.beat_id]: r.response_data.text }), {})
      if (Object.keys(restoredChoices).length > 0) setCommittedChoices(restoredChoices)
      if (Object.keys(restoredInputs).length > 0) setSubmittedInputs(restoredInputs)
      // Review: hydrate stored AI content so replay is fully offline — the
      // session's `generated` responses hold the character/narrative/resolution
      // text produced during the original playthrough. Seeding generatedContent
      // from them means the auto-generate path (also disabled in review) never
      // needs to run, so review costs zero AI generation.
      if (reviewMode) {
        const restoredGenerated = responses
          .filter((r) => r.response_type === 'generated' && isRenderableContent(r.response_data))
          .reduce((acc, r) => ({ ...acc, [r.beat_id]: { data: r.response_data, loading: false } }), {})
        if (Object.keys(restoredGenerated).length > 0) setGeneratedContent(restoredGenerated)
      }
      setHasRestoredPosition(true)
    }
  }, [serverSession, hasRestoredPosition, reviewMode])

  const recordResponseMutation = useMutation({
    mutationFn: (data) => playerSessionService.recordResponse(serverSession?.id, data),
    retry: 1,
    onError: (err) => {
      console.warn('Failed to record response after retry:', err)
      notifications.show({
        title: 'Response may not have been saved',
        message: 'Your choice was recorded locally but may not sync. Try refreshing if this persists.',
        color: 'yellow',
        autoClose: 8000,
      })
    },
  })

  const updatePositionMutation = useMutation({
    mutationFn: (data) => playerSessionService.updatePosition(serverSession?.id, data),
    onError: (err) => console.warn('Failed to update position:', err),
  })

  const completeSessionMutation = useMutation({
    mutationFn: () => playerSessionService.completeSession(serverSession?.id),
    onError: (err) => console.warn('Failed to complete session:', err),
  })

  const generateBeatContentMutation = useMutation({
    mutationFn: (beatId) => playerSessionService.generateBeatContent(serverSession?.id, beatId),
  })

  // Separate mutation instance for look-ahead generation so it doesn't
  // clobber the current beat's onSuccess callback (React Query drops
  // per-call callbacks when .mutate() is called again on the same instance)
  const lookAheadContentMutation = useMutation({
    mutationFn: (beatId) => playerSessionService.generateBeatContent(serverSession?.id, beatId),
  })

  // Fetch all threads
  const { data: threadsResponse, isLoading: threadsLoading } = useQuery({
    queryKey: ['storyThreads', storyId, 'player'],
    queryFn: () => storyService.getStoryThreads(storyId, { limit: 1000 }),
    enabled: !!storyId,
  })

  const allThreads = useMemo(
    () => (threadsResponse?.data || []).filter((t) => t.active !== false).sort((a, b) => a.position - b.position),
    [threadsResponse]
  )

  // Review: unlock the entire story map. A completed story is fully seen, so the
  // effective live edge (the review boundary) is its last beat — deriving it here
  // (rather than via a setState effect) makes isPositionReachable true for every
  // thread/beat, enabling free navigation, with no extra render pass. In live
  // play the edge is the real state that grows as the player advances.
  const effectiveLiveThreadIndex = reviewMode && allThreads.length > 0
    ? allThreads.length - 1
    : liveThreadIndex
  const effectiveLiveBeatIndex = reviewMode && allThreads.length > 0
    ? Math.max(0, (allThreads[allThreads.length - 1]?.beats_count || 1) - 1)
    : liveBeatIndex

  const currentThread = allThreads[currentThreadIndex] || null

  // Fetch beats for current thread
  const { data: beatsResponse, isLoading: beatsLoading } = useQuery({
    queryKey: ['beats', currentThread?.id],
    queryFn: () => storyService.getBeats(currentThread.id),
    enabled: !!currentThread?.id,
  })

  const beatsData = beatsResponse?.data || []

  /**
   * Called on every beat change. Persists the new position to the server and
   * auto-generates AI content for response beats (character_response,
   * narrative_response). Also performs look-ahead generation for the next beat
   * when the current beat doesn't require user input.
   * @param {Object} beat - The beat the player just navigated to
   * @param {number} beatIndex - Zero-based index within the current thread
   */
  const handlePositionChange = useCallback((beat, beatIndex) => {
    // Restore a previously committed choice so a revisited decision shows the
    // locked answer; otherwise clear the in-progress selection. A decision the
    // player hasn't advanced past yet has no committed choice, so it stays
    // editable on the forward edge.
    setSelectedChoice(committedChoices[beat.id] || null)
    // Restore previous input when navigating back to an input beat
    if (beat.beat_type === 'input' && submittedInputs[beat.id]) {
      setInputValue(submittedInputs[beat.id])
    } else {
      setInputValue('')
    }
    // Grow the live edge when the player advances within the furthest thread
    // they've reached. Browsing an earlier thread (a lower currentThreadIndex)
    // must never push the edge back, so only the live thread updates the beat.
    setLiveThreadIndex((prevThread) => {
      if (currentThreadIndex > prevThread) {
        setLiveBeatIndex(beatIndex)
        return currentThreadIndex
      }
      if (currentThreadIndex === prevThread) {
        setLiveBeatIndex((prevBeat) => Math.max(prevBeat, beatIndex))
      }
      return prevThread
    })
    if (!serverSession?.id || !currentThread) return

    // Review is strictly read-only: never persist position, never generate. The
    // completed session's position/responses must be left exactly as they are
    // (the whole reason review can't reuse the /play create flow), and all AI
    // content is already hydrated from stored `generated` responses.
    if (reviewMode) return

    // Don't persist any position while restore is unresolved — the mount-time
    // landing on beat 0 would overwrite the saved beat before the restore goTo
    // runs, permanently resetting a returning player to the start. The latch
    // opens once restore lands (or there's nothing to restore) and never closes
    // again, so normal forward play and review always persist.
    if (!restoreResolvedRef.current) return

    updatePositionMutation.mutate({
      thread_id: currentThread.id,
      beat_id: beat.id,
      thread_index: currentThreadIndex,
      beat_index: beatIndex,
    })

    // Auto-generate content for character_response and narrative_response beats
    // Both types now stream via ActionCable — streaming flag enables progressive display
    const isResponseBeat = beat.beat_type === 'character_response' || beat.beat_type === 'narrative_response' || beat.beat_type === 'resolution'
    if (isResponseBeat && !generatedContent[beat.id]) {
      setGeneratedContent((prev) => ({
        ...prev,
        [beat.id]: { data: null, loading: true, streaming: true },
      }))

      generateBeatContentMutation.mutate(beat.id, {
        // HTTP response is the canonical source when it contains renderable content.
        // If empty/invalid OR the backend Narrator fallback, preserve streaming
        // text so content doesn't vanish under the user.
        onSuccess: (data) => {
          setGeneratedContent((prev) => {
            if (!shouldReplaceWithHttpData(data, prev[beat.id])) {
              return { ...prev, [beat.id]: { ...prev[beat.id], loading: false, streaming: false } }
            }
            return { ...prev, [beat.id]: { data, loading: false, streaming: false } }
          })
        },
        onError: () => {
          setGeneratedContent((prev) => {
            if (isRenderableContent(prev[beat.id]?.data)) {
              return { ...prev, [beat.id]: { ...prev[beat.id], loading: false, streaming: false } }
            }
            return { ...prev, [beat.id]: { data: null, loading: false, streaming: false } }
          })
        },
      })
    }

    // Look-ahead: pre-generate next response beat while user reads current one
    // Skip when current beat requires user input — response content depends on their choice
    const requiresInput = beat.beat_type === 'decision' || beat.beat_type === 'input'
    const activeBeats = (beatsData || []).filter((b) => b.active !== false)
    const nextIdx = beatIndex + 1
    if (!requiresInput && nextIdx < activeBeats.length) {
      const nextBeatData = activeBeats[nextIdx]
      const isNextResponse = nextBeatData.beat_type === 'character_response' || nextBeatData.beat_type === 'narrative_response' || nextBeatData.beat_type === 'resolution'
      if (isNextResponse && !generatedContent[nextBeatData.id]) {
        setGeneratedContent((prev) => ({
          ...prev,
          [nextBeatData.id]: { data: null, loading: true, streaming: true },
        }))
        lookAheadContentMutation.mutate(nextBeatData.id, {
          onSuccess: (data) => {
            setGeneratedContent((prev) => {
              if (!shouldReplaceWithHttpData(data, prev[nextBeatData.id])) {
                return { ...prev, [nextBeatData.id]: { ...prev[nextBeatData.id], loading: false, streaming: false } }
              }
              return { ...prev, [nextBeatData.id]: { data, loading: false, streaming: false } }
            })
          },
          onError: () => {
            setGeneratedContent((prev) => {
              if (isRenderableContent(prev[nextBeatData.id]?.data)) {
                return { ...prev, [nextBeatData.id]: { ...prev[nextBeatData.id], loading: false, streaming: false } }
              }
              return { ...prev, [nextBeatData.id]: { data: null, loading: false, streaming: false } }
            })
          },
        })
      }
    }
  }, [serverSession?.id, currentThread, currentThreadIndex, generatedContent, beatsData, committedChoices, submittedInputs, reviewMode])

  /**
   * Record the player's response to the server (fire-and-forget).
   * @param {string|number} beatId - Beat the response belongs to
   * @param {string|number} threadId - Parent thread ID
   * @param {string} beatType - One of the 6 beat type constants
   * @param {string} responseType - 'choice' or 'text'
   * @param {Object} responseData - The actual response payload
   */
  const handleRecordResponse = useCallback((beatId, threadId, beatType, responseType, responseData) => {
    if (!serverSession?.id) return Promise.resolve()

    const payload = {
      beat_id: beatId,
      thread_id: threadId,
      beat_type: beatType,
      response_type: responseType,
      response_data: responseData,
    }

    // Await critical player input (choices/text) so they persist before advancing.
    // Generated content stays fire-and-forget — it's reproducible server-side.
    if (responseType === 'choice' || responseType === 'text') {
      return recordResponseMutation.mutateAsync(payload)
    }

    recordResponseMutation.mutate(payload)
  }, [serverSession?.id])

  /**
   * Called when the player finishes all beats in the current thread.
   * Advances to the next thread, or marks the entire session complete
   * if this was the last thread.
   */
  const handleThreadComplete = useCallback(() => {
    // Reviewing means the player is walking forward through a thread they've
    // already finished (it sits behind the live edge). Crossing its end just
    // continues the review into the next thread — no title card (already seen),
    // and never completes the session, since the live edge is still ahead. In
    // read-only review the whole story is behind the (story-end) edge, so every
    // thread crossing is a review pass.
    const isReviewing = currentThreadIndex < effectiveLiveThreadIndex

    if (currentThreadIndex < allThreads.length - 1) {
      // Only surface the title card when genuinely entering a new thread for the
      // first time; a review pass slips straight into the next thread's beat 0.
      setShowTitleCard(!isReviewing)
      setCurrentThreadIndex((prev) => prev + 1)
    } else if (reviewMode) {
      // Review reached the last thread's end — the walk-through is done. Never
      // complete or reopen the session; just stop at the final beat. (The last
      // thread has currentThreadIndex === liveThreadIndex, so isReviewing is
      // false there; this guard keeps the completion path below unreachable.)
    } else if (!isReviewing) {
      // Finished the last RELEASED thread. allThreads only ever contains the
      // released (active) threads — the player API filters out locked ones — so
      // "last released" is NOT the same as "last in the story". Only complete
      // when this really is the story's final thread; otherwise the facilitator
      // simply hasn't released the next session yet, and the session must remain
      // in_progress so it re-opens on release (see isWaitingForRelease).
      const totalThreads = story?.threads_count ?? allThreads.length
      const isFinalThreadOfStory = allThreads.length >= totalThreads

      if (isFinalThreadOfStory) {
        setIsSessionComplete(true)
        if (serverSession?.id) {
          completeSessionMutation.mutate()
        }
      } else {
        setIsWaitingForRelease(true)
      }
    }
  }, [currentThreadIndex, effectiveLiveThreadIndex, allThreads.length, story?.threads_count, serverSession?.id, reviewMode])

  // Restore beat position after server session loads — startIndex may have been 0
  // on initial render because serverSession wasn't available yet
  const [hasRestoredBeat, setHasRestoredBeat] = useState(false)

  // Suppress-persist guard against the resume clobber. A returning player's
  // saved beat must not be overwritten by the mount-time landing on beat 0: the
  // player can momentarily sit at beat 0 before the restore goTo runs (e.g. beats
  // load before the session does), and persisting that 0 permanently resets them
  // to the start (the team-resume "started over" bug Krysten hit). update_position
  // has no server-side regression guard, so we HOLD position writes until restore
  // is resolved — then latch open forever (never re-suppress, so normal forward
  // play and review always persist).
  //
  // "Resolved" = either the loaded session has no mid-thread beat to restore
  // (current_beat_index 0 — the natural beat-0 landing is correct to persist), or
  // we've landed the restore goTo (hasRestoredBeat). Resolved in an effect (not
  // during render) so we never mutate a ref mid-render. The window before this
  // effect runs is safe: the only position write that could fire then is the
  // mount-time beat-0 landing, which handlePositionChange holds while the latch
  // is closed. The restore effect also opens the latch synchronously right before
  // its goTo so that landing persists.
  useEffect(() => {
    if (restoreResolvedRef.current) return
    if (!serverSession) return // not loaded — onPositionChange is no-op'd anyway
    if (!(serverSession.current_beat_index > 0) || hasRestoredBeat) {
      restoreResolvedRef.current = true
    }
  }, [serverSession, hasRestoredBeat])

  // Resume start index
  const resumeStartIndex = useMemo(() => {
    if (hasRestoredBeat) return 0
    if (!serverSession || !currentThread) return 0
    if (serverSession.current_thread_id === currentThread.id && serverSession.current_beat_index > 0) {
      return serverSession.current_beat_index
    }
    return 0
  }, [serverSession, currentThread, hasRestoredBeat])

  const player = useThreadPlayer(beatsData, {
    resetKey: currentThread?.id,
    startIndex: goToLastBeat
      ? (beatsData || []).filter((b) => b.active !== false).length - 1
      : resumeStartIndex,
    onComplete: handleThreadComplete,
    onPositionChange: handlePositionChange,
  })

  useEffect(() => {
    if (hasRestoredPosition && !hasRestoredBeat && resumeStartIndex > 0 && player.beatCount > 0) {
      // Resuming partway into a thread — the player has already passed its
      // start, so skip the title card and drop them at their saved beat.
      setShowTitleCard(false)
      // Open the latch BEFORE goTo so the onPositionChange it fires (landing on
      // the saved beat) IS persisted — only the spurious beat-0 landings before
      // this point were suppressed.
      restoreResolvedRef.current = true
      player.goTo(resumeStartIndex)
      setHasRestoredBeat(true)
    }
  }, [hasRestoredPosition, hasRestoredBeat, resumeStartIndex, player.beatCount])

  // Apply a pending cross-thread jump once the target thread's beats have
  // loaded. We switched currentThreadIndex when the jump was requested; now that
  // the player has reset onto the new thread we land it on the requested beat.
  useEffect(() => {
    if (pendingBeatIndex == null) return
    if (!currentThread?.id || player.beatCount === 0) return
    const target = Math.min(pendingBeatIndex, player.beatCount - 1)
    player.goTo(target)
    setPendingBeatIndex(null)
  }, [pendingBeatIndex, currentThread?.id, player.beatCount, player.goTo])

  // Clear goToLastBeat flag after it takes effect
  useEffect(() => {
    if (goToLastBeat) setGoToLastBeat(false)
  }, [goToLastBeat])

  // Reset when story changes. Skipped in review mode: there, storyId resolves
  // late (null → the loaded session's story), and running this reset on that
  // transition would clobber the freshly-hydrated review state (committed
  // choices + stored AI content) that the restore effect just populated. A
  // review is always a fresh mount per /review/:sessionId, so state already
  // starts clean and there is nothing to reset.
  useEffect(() => {
    if (reviewMode) return
    setCurrentThreadIndex(0)
    setHasRestoredPosition(false)
    setHasRestoredBeat(false)
    // Re-close the clobber latch so the new story's saved position is protected
    // until its own restore resolves.
    restoreResolvedRef.current = false
    setGeneratedContent({})
    setSubmittedInputs({})
    setCommittedChoices({})
    setLiveThreadIndex(0)
    setLiveBeatIndex(0)
    setPendingBeatIndex(null)
    setIsSessionComplete(false)
    setShowTitleCard(true)
  }, [storyId, reviewMode])

  /**
   * Navigate backward. Within a thread, delegates to player.prev().
   * At the first beat of a thread, jumps to the last beat of the previous
   * thread (cross-thread navigation) by setting the goToLastBeat flag.
   */
  const handlePrev = useCallback(() => {
    if (player.canGoPrev) {
      player.prev()
    } else if (currentThreadIndex > 0) {
      // Stepping back into the previous thread's last beat — not a fresh entry,
      // so don't re-show that thread's title card.
      setShowTitleCard(false)
      setGoToLastBeat(true)
      setCurrentThreadIndex((prev) => prev - 1)
    }
  }, [player.canGoPrev, player.prev, currentThreadIndex])

  /**
   * Whether a (thread, beat) position has been seen this session and is thus
   * reachable for review. Everything in a thread before the live thread is fully
   * seen; the live thread is seen up to the live beat; nothing beyond the live
   * edge is reachable.
   */
  const isPositionReachable = useCallback((threadIndex, beatIndex) => {
    if (threadIndex < 0 || beatIndex < 0) return false
    if (threadIndex < effectiveLiveThreadIndex) return true
    if (threadIndex === effectiveLiveThreadIndex) return beatIndex <= effectiveLiveBeatIndex
    return false
  }, [effectiveLiveThreadIndex, effectiveLiveBeatIndex])

  /**
   * Jump directly to any already-seen position in the story via the timeline,
   * across threads. Rejects positions past the live edge. Within the current
   * thread it's an immediate goTo; into another thread it switches threads and
   * defers the beat landing until that thread's beats load (pendingBeatIndex).
   * Pure navigation — never re-records or regenerates, so answers stay locked.
   * @param {number} threadIndex - Target thread index
   * @param {number} beatIndex - Target beat index within that thread
   */
  const jumpToPosition = useCallback((threadIndex, beatIndex) => {
    if (!isPositionReachable(threadIndex, beatIndex)) return
    if (threadIndex === currentThreadIndex) {
      if (beatIndex !== player.currentIndex) player.goTo(beatIndex)
      return
    }
    // Cross-thread: a reviewed thread is already seen, so never re-show its title
    // card. Switch threads, then land the beat once it loads.
    setShowTitleCard(false)
    setPendingBeatIndex(beatIndex)
    setCurrentThreadIndex(threadIndex)
  }, [isPositionReachable, currentThreadIndex, player.goTo, player.currentIndex])

  /**
   * Skip back to the live edge — the player's furthest-reached position — from
   * anywhere in the review history. The "catch up to where I was" control.
   */
  const skipToCurrent = useCallback(() => {
    jumpToPosition(effectiveLiveThreadIndex, effectiveLiveBeatIndex)
  }, [jumpToPosition, effectiveLiveThreadIndex, effectiveLiveBeatIndex])

  /**
   * Remove cached AI-generated content for all beats after the given index.
   * Called when the player changes a choice or input so that downstream
   * response beats regenerate with the updated context.
   * @param {number} afterIndex - Beat index; all beats after this are cleared
   */
  const clearDownstreamContent = useCallback((afterIndex) => {
    const activeBeats = player.beats
    if (!activeBeats?.length) return
    const idsToRemove = activeBeats.slice(afterIndex + 1).map((b) => b.id)
    if (idsToRemove.length === 0) return
    setGeneratedContent((prev) => {
      const next = { ...prev }
      idsToRemove.forEach((id) => delete next[id])
      return next
    })
    clearBeats(idsToRemove)
  }, [player.beats, clearBeats])

  /**
   * Navigate forward. If the player has selected a choice on a decision beat,
   * records it to the server and clears downstream generated content before
   * advancing so response beats regenerate with the new choice context.
   */
  const handleNext = useCallback(async () => {
    // Review is read-only: Next only advances the walk-through. Never record,
    // commit, or clear downstream content (all answers are already locked and
    // hydrated from the completed session).
    if (reviewMode) {
      player.next()
      return
    }
    const beat = player.currentBeat
    // A beat whose choice is already committed is locked: the player is just
    // stepping forward through history. Don't re-record or wipe downstream
    // content — the answer (and everything it generated) is fixed for the session.
    const isLockedChoice = !!(beat && committedChoices[beat.id])

    if (selectedChoice && beat && currentThread && !isLockedChoice) {
      try {
        await handleRecordResponse(
          beat.id,
          currentThread.id,
          beat.beat_type,
          'choice',
          { choice_id: selectedChoice.id, choice_text: selectedChoice.text }
        )
      } catch (err) {
        // Notification already shown by mutation onError — don't block advancement
      }
      // Commit the choice so this beat is locked on any future revisit.
      setCommittedChoices((prev) => ({ ...prev, [beat.id]: selectedChoice }))
      setSelectedChoice(null)
      // Clear downstream generated content so response beats regenerate with new choice
      clearDownstreamContent(player.currentIndex)
    }
    player.next()
  }, [player.next, selectedChoice, player.currentBeat, currentThread, handleRecordResponse, clearDownstreamContent, player.currentIndex, committedChoices, reviewMode])

  const handleChoiceSelect = useCallback((choice) => {
    setSelectedChoice(choice)
  }, [])

  const handleInputSubmit = useCallback(async () => {
    // Review is read-only: advancing past an input beat just moves forward.
    if (reviewMode) {
      player.next()
      return
    }
    if (!player.currentBeat || !currentThread || !inputValue.trim()) return
    // A previously submitted input is locked — stepping forward shouldn't
    // re-record it or regenerate downstream content. Just advance.
    if (submittedInputs[player.currentBeat.id]) {
      player.next()
      return
    }
    const trimmed = inputValue.trim()
    // Save submitted text so it can be restored when navigating back
    setSubmittedInputs((prev) => ({ ...prev, [player.currentBeat.id]: trimmed }))
    const qualityHint = checkInputQuality(trimmed)
    try {
      await handleRecordResponse(
        player.currentBeat.id,
        currentThread.id,
        player.currentBeat.beat_type,
        'text',
        { text: trimmed, ...(qualityHint && { quality_hint: qualityHint }) }
      )
    } catch (err) {
      // Notification already shown by mutation onError — don't block advancement
    }
    // Clear downstream generated content so response beats regenerate with new input
    clearDownstreamContent(player.currentIndex)
    setInputValue('')
    player.next()
  }, [player.currentBeat, currentThread, handleRecordResponse, inputValue, player.next, clearDownstreamContent, player.currentIndex, submittedInputs, reviewMode])

  const handleReset = useCallback(() => {
    setCurrentThreadIndex(0)
    setGeneratedContent({})
    setSubmittedInputs({})
    setCommittedChoices({})
    setLiveThreadIndex(0)
    setLiveBeatIndex(0)
    setPendingBeatIndex(null)
    setSelectedChoice(null)
    setInputValue('')
    setIsSessionComplete(false)
    setIsWaitingForRelease(false)
    setShowTitleCard(true)
    player.reset()
  }, [player.reset])

  // A waiting player auto-resumes the moment the next session is released: the
  // threads query refetches, allThreads grows past where they stopped, and we
  // advance them into the freshly-released thread. Without this they'd have to
  // hard-refresh to escape the waiting screen.
  useEffect(() => {
    if (!isWaitingForRelease) return
    if (currentThreadIndex < allThreads.length - 1) {
      setIsWaitingForRelease(false)
      setShowTitleCard(true)
      setCurrentThreadIndex((prev) => prev + 1)
    }
  }, [isWaitingForRelease, currentThreadIndex, allThreads.length])

  const dismissTitleCard = useCallback(() => setShowTitleCard(false), [])

  // Overall progress
  const totalBeatsInStory = allThreads.reduce((sum, t) => sum + (t.beats_count || 0), 0)
  const beatsBeforeCurrent =
    allThreads.slice(0, currentThreadIndex).reduce((sum, t) => sum + (t.beats_count || 0), 0) +
    player.currentIndex
  const overallProgress = totalBeatsInStory > 0 ? ((beatsBeforeCurrent + 1) / totalBeatsInStory) * 100 : 0

  const canGoPrev = player.canGoPrev || currentThreadIndex > 0
  const canGoNext = player.canGoNext || currentThreadIndex < allThreads.length - 1

  // The current beat is locked when its answer was already committed this
  // session: a decision with a committed choice, or an input with submitted
  // text. Locked beats render their answer read-only and Next merely advances.
  const currentBeatId = player.currentBeat?.id
  const isCurrentBeatLocked =
    !!currentBeatId &&
    (!!committedChoices[currentBeatId] || !!submittedInputs[currentBeatId])

  // Merge ActionCable streaming text into generated content for response beats
  const isResponseBeatType = player.currentBeat?.beat_type === 'narrative_response' || player.currentBeat?.beat_type === 'character_response' || player.currentBeat?.beat_type === 'resolution'
  const currentGeneratedContent = useMemo(() => {
    if (!player.currentBeat) return null
    const beatId = player.currentBeat.id
    const stored = generatedContent[beatId]
    const stream = streamingContent[beatId]

    // Once HTTP delivers final structured data, use it as the canonical source —
    // but only if the data is actually renderable (non-empty string or array).
    // Empty arrays, objects, or null would shadow the streaming text fallback.
    if (isRenderableContent(stored?.data) && !stored?.loading) return stored

    // While HTTP is pending (or returned empty), show streaming progress for response beats
    if (stream?.text && isResponseBeatType) {
      return { data: stream.text, loading: false, streaming: !stream.complete }
    }

    // If regenerating a non-streaming beat, show loading — don't fall through to old stream data
    if (stored?.loading && !stored?.streaming) return stored

    return stored?.loading ? stored : null
  }, [player.currentBeat, generatedContent, streamingContent, isResponseBeatType])

  // Only surface the title card when we're genuinely at the start of a loaded
  // thread (beat 0). Guards against showing it mid-thread or before beats load.
  const displayTitleCard =
    showTitleCard &&
    !!currentThread &&
    !threadsLoading &&
    !beatsLoading &&
    player.currentIndex === 0 &&
    !isSessionComplete &&
    !isWaitingForRelease

  return {
    story,
    reviewMode,
    serverSessionId: serverSession?.id,
    sessionRole: serverSession?.role || null,
    // Solo vs team play, for the player-header indicator. In live play this is
    // driven by the URL params (teamContext) with the name from the cached team
    // query. In review there are no URL params, so it comes from the loaded
    // session's team fields (team_name added to the serializer for this).
    isTeamPlay: reviewMode ? !!serverSession?.team_id : !!teamContext,
    teamName: reviewMode ? (serverSession?.team_name || null) : (teamName || null),
    showTitleCard: displayTitleCard,
    onTitleCardNext: dismissTitleCard,
    needsRoleSelection,
    playerRoles,
    takenRoleKeys,
    selectRole: roleMutation.mutate,
    roleSaving: roleMutation.isPending,
    allThreads,
    currentThread,
    currentThreadIndex,
    currentBeat: player.currentBeat,
    currentBeatTypeDef: player.currentBeatTypeDef,
    currentIndex: player.currentIndex,
    beats: player.beats,
    beatCount: player.beatCount,
    // Committed decision answers this session, keyed by beat id ({ id, text }).
    // Exposed for the redesigned player's Recall Banner; read-only to consumers.
    committedChoices,
    liveThreadIndex: effectiveLiveThreadIndex,
    liveBeatIndex: effectiveLiveBeatIndex,
    isCurrentBeatLocked,
    isPositionReachable,
    jumpToPosition,
    skipToCurrent,
    progress: player.progress,
    overallProgress,
    selectedChoice,
    inputValue,
    generatedContent: currentGeneratedContent,
    isSessionComplete,
    isWaitingForRelease,
    isLoading: reviewSessionLoading || storyLoading || threadsLoading,
    beatsLoading,
    canGoPrev,
    canGoNext,
    handleNext,
    handlePrev,
    handleChoiceSelect,
    handleInputSubmit,
    handleReset,
    setInputValue,
  }
}
